import React, { useMemo } from "react";
import { DataSourcesStore } from "../stores/DataSourcesStore";
import { Box } from "@mui/material";
import { observer } from "mobx-react";

import MapFields from "./MapFields";
import KeyFields from "./KeyFields";

import { emptyKeyField } from "../helpers/emptyStates";
import ErrorHandler from "../components/ErrorHandler";

type TargetMappingProps = {
    store: DataSourcesStore;
};

const TargetMapping: React.FC<TargetMappingProps> = observer(({ store }) => {
    const {
        setup,
        sourceFields,
        setSourceFields,
        conditionRules,
        concatFields,
        keyFields,
        setKeyFields,
        updateSelectedTargetFields,
        getUpdatedTargetFields,
        shouldApplyConditions,
        shouldConcatenateFields,
        hasError,
        checkIfKeyFieldsAreValid,
        targetFields,
        selectedTargetFields,
    } = store;

    const hasKeyFieldsError =
        hasError.keyFieldsMissing ||
        hasError.keyFieldsSourceMissingTarget ||
        hasError.keyFieldsCoreSourceAndTarget;

    const updateSourceFields = (
        id: string,
        key: string,
        value: boolean | string,
        targetFieldId?: string,
    ) => {
        if (key === "targetFieldName") {
            updateSelectedTargetFields({
                targetFieldId,
                sourceFieldId: id,
                action: value ? "add" : "remove",
            });
        }

        setSourceFields(
            sourceFields.map((field) => {
                if (field.id === id) {
                    return {
                        ...field,
                        [key]: value,
                    };
                }
                return field;
            }),
        );

        if (hasKeyFieldsError) {
            checkIfKeyFieldsAreValid();
        }
    };

    const addKeyField = () => {
        setKeyFields([...keyFields, emptyKeyField()]);
    };

    const updateKeyField = (
        id: string,
        key: string,
        value: string,
        targetFieldId?: string,
    ) => {
        updateSelectedTargetFields({
            sourceFieldId: id,
            targetFieldId,
            action: value ? "add" : "remove",
        });

        setKeyFields(
            keyFields.map((field) => {
                if (field.id === id) {
                    return {
                        ...field,
                        [key]: value,
                    };
                }
                return field;
            }),
        );

        if (hasKeyFieldsError) {
            checkIfKeyFieldsAreValid();
        }
    };

    const removeKeyField = (id: string) => {
        updateSelectedTargetFields({ sourceFieldId: id, action: "remove" });
        setKeyFields(keyFields.filter((field) => field.id !== id));
    };

    const sourceFieldOptions = useMemo(
        () =>
            sourceFields.map((field) => ({
                label: field.alias,
                value: field.alias,
            })),
        [sourceFields],
    );

    const keyFieldSourceOptions = useMemo(() => {
        return sourceFields
            .map((field) => ({
                label: field.alias + " → " + field.targetFieldName,
                value: field.targetFieldName,
            }))
            .filter((option) => option.value); // Only show options that have a target field set
    }, [sourceFields]);

    const sourcesWithCoreTargets = sourceFields
        .filter((field) => {
            const targetFieldId = selectedTargetFields.find(
                (stf) => stf.sourceFieldId === field.id,
            )?.targetFieldId;

            return targetFields.some((tf) => tf.id === targetFieldId);
        })
        .map((f) => f.targetFieldName);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <ErrorHandler
                hasError={hasKeyFieldsError}
                errorMessage={`${
                    hasError.keyFieldsMissing
                        ? "Please setup at least one key field."
                        : ""
                }
                ${
                    hasError.keyFieldsSourceMissingTarget
                        ? "Please ensure each source field used in key fields has a target."
                        : ""
                }
                ${
                    hasError.keyFieldsCoreSourceAndTarget
                        ? "Please ensure each source field used in key fields has a valid 'Key Field for Target'. If the source has a core field as a target, the 'Key Field For Target' cannot also be a core field."
                        : ""
                }`}
            />
            {setup.sourceType === "batch" && (
                <KeyFields
                    keyFields={keyFields}
                    sourceFieldOptions={keyFieldSourceOptions}
                    getUpdatedTargetFields={getUpdatedTargetFields}
                    addKeyField={addKeyField}
                    updateKeyField={updateKeyField}
                    removeKeyField={removeKeyField}
                    sourcesWithCoreTargets={sourcesWithCoreTargets}
                />
            )}
            <MapFields
                sourceFields={sourceFields}
                conditionRules={conditionRules}
                concatFields={concatFields}
                updateSourceFields={updateSourceFields}
                getUpdatedTargetFields={getUpdatedTargetFields}
                sourceFieldOptions={sourceFieldOptions}
                shouldApplyConditions={shouldApplyConditions}
                shouldConcatenateFields={shouldConcatenateFields}
            />
        </Box>
    );
});

export default TargetMapping;
